import { ApiLevel } from '../../models/api/api-level.interface';
import { ApiBuilding, ApiHeatLoadCalculation, ApiRoom, ApiSlab, CalculationSummary, Level, SlabType } from '../../models';
import { extractCalculationSummary } from './building.domain';
import { BuildingType, HeatLoadCalculationMethod } from '../../../../lib';
import { DEFAULT_IS_COOLED, DEFAULT_IS_HEATED } from '../../consts';

/*
 * Currently we have only 1 floor slab per room with 1 zone representing the entire floor area
 */
const getRoomArea = (apiSlabs: { [key: string]: ApiSlab }, targetApiRoom: ApiRoom): number | undefined => {
    const floorSlab = Object.values(apiSlabs)
        .filter(apiSlab => apiSlab.type === SlabType.FLOOR)
        // I know that there is only one floor slab per room, so it's safe to take the first match
        .find(slab => targetApiRoom.slabIds?.includes(slab.id));

    return floorSlab?.zones.reduce((acc, zone) => acc + (zone.area || 0), 0);
};

export const mapApiBuildingToLevelsView = (apiBuilding?: ApiBuilding): Level[] => {
    if (!apiBuilding) return [];

    const apiLevels = apiBuilding.levels || {};
    const apiRooms = apiBuilding.rooms || {};
    const apiSlabs = apiBuilding.slabs || {};
    const calculations = apiBuilding.heatLoadCalculations || [];

    const rbrCalculations = calculations.find(calc => calc.method == HeatLoadCalculationMethod.RoomByRoom);

    return Object.values(apiLevels)
        .map(apiLevel => ({
            id: apiLevel.id,
            name: apiLevel.name,
            number: apiLevel.number,
            roomsData: Object.values(apiRooms)
                .filter(apiRoom => apiRoom.levelIds.includes(apiLevel.id))
                .map(apiRoom => ({
                    id: apiRoom.id,
                    name: apiRoom.name,
                    type: apiRoom.type,
                    height: apiRoom.height,
                    isHeated: apiRoom.isHeated,
                    isCooled: apiRoom.isCooled,
                    heatingLoad: rbrCalculations?.roomCalculations?.[apiRoom.id]?.baseHeatLoad,
                    coolingLoad: rbrCalculations?.roomCalculations?.[apiRoom.id]?.baseCoolingLoad,
                    area: getRoomArea(apiSlabs, apiRoom),
                    slabs: apiRoom.slabIds?.map(slabId => apiSlabs[slabId])
                }))
        }))
        .sort((a, b) => b.number - a.number);
};

export const mapApiBuildingToHeatLoad = (
    apiBuilding?: ApiBuilding
):
    | {
          baseHeatLoad: number;
          warmWaterHeatLoad?: number;
          lockTimeHeatLoad?: number;
          baseCoolingLoad?: number;
          includeTotalBuilding?: boolean;
      }
    | undefined => {
    if (!apiBuilding) return undefined;

    const calculations = apiBuilding.heatLoadCalculations || [];
    const rbrCalculations = calculations.find(calc => calc.method == HeatLoadCalculationMethod.RoomByRoom);

    if (!rbrCalculations) return undefined;

    return {
        baseHeatLoad: rbrCalculations.baseHeatLoad,
        warmWaterHeatLoad: rbrCalculations?.warmWaterHeatLoad,
        lockTimeHeatLoad: rbrCalculations?.lockTimeHeatLoad,
        baseCoolingLoad: rbrCalculations?.baseCoolingLoad,
        includeTotalBuilding: rbrCalculations?.roomCalculations?.['leftover-area']?.isActive
    };
};

export const mapLevelsToApiModel = (levelsArray: Level[]): { [key: string]: ApiLevel } => {
    const levelsDict: { [key: string]: ApiLevel } = {};

    levelsArray.forEach(level => {
        levelsDict[level.id] = {
            id: level.id,
            name: level.name,
            number: level.number
        };
    });

    return levelsDict;
};

export const mapRoomsToApiModel = (
    levelsArray: Level[],
    previousRoomsSnapshot: { [key: string]: ApiRoom },
    defaultRoomHeight: number
): { [key: string]: ApiRoom } => {
    const roomsDict: { [key: string]: ApiRoom } = {};

    levelsArray.forEach(level => {
        level.roomsData.forEach(room => {
            const previousRoom = previousRoomsSnapshot[room.id] as ApiRoom | undefined;

            roomsDict[room.id] = {
                // Preserve API fields not represented in the UI model.
                ...previousRoom,
                id: room.id,
                name: room.name,
                type: room.type,
                slabIds: room.slabs?.map(slab => slab.id),
                levelIds: [level.id],
                height: room.height ?? previousRoom?.height ?? defaultRoomHeight,
                isHeated: room.isHeated ?? previousRoom?.isHeated ?? DEFAULT_IS_HEATED,
                isCooled: room.isCooled ?? previousRoom?.isCooled ?? DEFAULT_IS_COOLED
            };
        });
    });

    return roomsDict;
};

export const mapSlabsToApiModel = (levelsArray: Level[], previousSlabsSnapshot: { [key: string]: ApiSlab }): { [key: string]: ApiSlab } => {
    const slabsDict: { [key: string]: ApiSlab } = {};

    levelsArray.forEach(level => {
        level.roomsData.forEach(room => {
            room.slabs?.forEach(slab => {
                const previousSlab = previousSlabsSnapshot[slab.id] as ApiSlab | undefined;

                slabsDict[slab.id] = { ...previousSlab, ...slab };
            });
        });
    });

    return slabsDict;
};

export const mapApiBuildingToCalculationSummary = (apiBuilding?: ApiBuilding): CalculationSummary | undefined => {
    if (!apiBuilding) return undefined;

    const totalLivingArea = apiBuilding.livingArea || 0;
    const { usedArea } = extractCalculationSummary(mapApiBuildingToLevelsView(apiBuilding));
    const heatLoad = mapApiBuildingToHeatLoad(apiBuilding);
    const totalHeatLoad = (heatLoad?.baseHeatLoad ?? 0) + (heatLoad?.lockTimeHeatLoad ?? 0) + (heatLoad?.warmWaterHeatLoad ?? 0);
    const totalCoolingLoad = heatLoad?.baseCoolingLoad ?? 0;
    const leftoverArea = totalLivingArea - usedArea;
    const includeTotalBuilding = heatLoad?.includeTotalBuilding ?? false;

    return { totalHeatLoad, totalCoolingLoad, totalLivingArea, leftoverArea, includeTotalBuilding };
};

export const mapHeatLoadCalculationsToApiModel = (
    previousCalculationsSnapshot: ApiHeatLoadCalculation[],
    buildingType?: string
): ApiHeatLoadCalculation[] => {
    const hasRoomByRoomCalculation = previousCalculationsSnapshot.find(calc => HeatLoadCalculationMethod.RoomByRoom === calc.method);

    if (hasRoomByRoomCalculation) {
        const mappedHeatLoadCalculations: ApiHeatLoadCalculation[] = previousCalculationsSnapshot.map(calc => {
            return {
                method: calc.method,
                baseHeatLoad: calc.baseHeatLoad ?? 0,
                ...(calc.baseCoolingLoad ? { baseCoolingLoad: calc.baseCoolingLoad } : {}),
                ...(calc.lockTimeHeatLoad ? { lockTimeHeatLoad: calc.lockTimeHeatLoad } : {}),
                ...(calc.warmWaterHeatLoad ? { warmWaterHeatLoad: calc.warmWaterHeatLoad } : {}),
                ...(calc.roomCalculations ? { roomCalculations: calc.roomCalculations } : {}),
                orderDate: calc.orderDate ?? new Date().toISOString()
            };
        });
        return mappedHeatLoadCalculations;
    }

    const initialCalculation: ApiHeatLoadCalculation = {
        method: HeatLoadCalculationMethod.RoomByRoom,
        baseHeatLoad: 0,
        orderDate: new Date().toISOString(),
        lockTimeHeatLoad: 0
    };

    if (buildingType === BuildingType.SingleFamilyHouse) {
        initialCalculation.warmWaterHeatLoad = 0;
    }

    return [initialCalculation];
};
